import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const LINKS = [
  { href: '#problem', label: 'Problem' },
  { href: '#approach', label: 'Reports' },
  { href: '#impact', label: 'Why' },
  { href: '#pricing', label: 'Engagement' },
  { href: '#faq', label: 'FAQ' },
];

export const LandingNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth() || {};
  const isAuthed = !!user;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <motion.nav
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-black/55 backdrop-blur-2xl border-b border-white/10' : 'bg-transparent'
      }`}
      data-testid="landing-nav"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3 group" data-testid="nav-logo">
          <span className="font-display text-white font-semibold text-lg leading-none tracking-tight border border-[#C9A66B]/45 rounded-full w-9 h-9 md:w-10 md:h-10 flex items-center justify-center group-hover:border-[#C9A66B] transition-colors">
            <span className="italic">E·N</span>
          </span>
          <span className="font-display text-white font-semibold tracking-tight text-lg md:text-xl">Epicurea Nexus</span>
        </a>

        <div className="hidden lg:flex items-center gap-1">
          {LINKS.map(l => (
            <a
              key={l.href}
              href={l.href}
              className="px-4 py-2 text-sm text-white/65 hover:text-white transition-colors duration-200"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-2">
          <Link
            to={isAuthed ? '/dashboard' : '/login'}
            className="px-4 py-2 rounded-full text-sm text-white/75 hover:text-white border border-white/15 hover:border-white/30 hover:bg-white/[0.04] transition-all duration-300"
            data-testid="nav-login-btn"
          >
            {isAuthed ? 'Dashboard' : 'Login'}
          </Link>
          <a
            href="#contact"
            className="px-4 py-2 rounded-full text-sm font-semibold bg-white text-[#28282B] hover:bg-white/90 transition-all duration-300"
            data-testid="nav-cta-btn"
          >
            Book a Diagnostic
          </a>
        </div>

        <button
          className="md:hidden w-10 h-10 rounded-full flex items-center justify-center bg-white/[0.04] border border-white/10 text-white"
          onClick={() => setMobileOpen(v => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-black/85 backdrop-blur-2xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map(l => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileOpen(false)}
                  className="py-3 text-white/75 hover:text-white text-base font-medium"
                >
                  {l.label}
                </a>
              ))}
              <div className="h-px bg-white/10 my-3" />
              <Link
                to={isAuthed ? '/dashboard' : '/login'}
                onClick={() => setMobileOpen(false)}
                className="py-3 text-white/75 text-base font-medium"
                data-testid="nav-mobile-login"
              >
                {isAuthed ? 'Dashboard' : 'Login'}
              </Link>
              <a
                href="#contact"
                onClick={() => setMobileOpen(false)}
                className="mt-2 inline-flex items-center justify-center rounded-full bg-white text-[#28282B] py-3 font-semibold"
                data-testid="nav-mobile-cta"
              >
                Book a Diagnostic
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default LandingNav;
